import { supabase } from '../client';

/**
 * Universal cross-org / cross-store search.
 *
 * Backed by `core.rpc_global_search`, which is SECURITY INVOKER: the RPC runs
 * as the calling user, so RLS on every underlying table is the access boundary.
 * Results therefore span EXACTLY the orgs + stores the caller can already read
 * — no org/store args, no leakage. See migration
 * `20260703240000_core_rpc_global_search.sql`.
 */

export type GlobalSearchEntityType =
  | 'client'
  | 'order'
  | 'shipment'
  | 'product'
  | 'lot'
  | 'wholesaler'
  | 'vendor'
  | 'contract'
  | 'appointment';

export interface GlobalSearchResult {
  entityType: GlobalSearchEntityType;
  entityId: string;
  orgId: string;
  /** Null for org-scoped catalog entities (product, lot). */
  storeId: string | null;
  title: string;
  subtitle: string | null;
  orgName: string | null;
  storeName: string | null;
  createdAt: string | null;
}

interface RawRow {
  entity_type: GlobalSearchEntityType;
  entity_id: string;
  org_id: string;
  store_id: string | null;
  title: string | null;
  subtitle: string | null;
  org_name: string | null;
  store_name: string | null;
  created_at: string | null;
}

/**
 * Search every accessible entity for `query`. Returns at most `perType` rows
 * per entity type (newest first), already mapped to camelCase. Queries shorter
 * than 2 chars short-circuit to an empty list (mirrors the RPC's own guard, and
 * avoids a needless round-trip).
 */
export async function globalSearch(
  query: string,
  perType = 6,
): Promise<GlobalSearchResult[]> {
  const term = query.trim();
  if (term.length < 2) return [];

  const { data, error } = await supabase()
    .schema('core')
    .rpc('rpc_global_search' as never, {
      p_query: term,
      p_per_type: perType,
    } as never);
  if (error) throw error;

  const rows = (data ?? []) as RawRow[];
  return rows.map((r) => ({
    entityType: r.entity_type,
    entityId: r.entity_id,
    orgId: r.org_id,
    storeId: r.store_id,
    title: r.title ?? '',
    subtitle: r.subtitle,
    orgName: r.org_name,
    storeName: r.store_name,
    createdAt: r.created_at,
  }));
}
